import { useState, useRef, useEffect } from "react";
import {
  Users, Plus, Trash2, Loader2, CheckCircle2, RefreshCw, Mail, KeyRound,
  MailCheck, MailWarning, FileSignature, Pencil, Car, Building2, Send, Search, X as XIcon, ScanLine,
} from "lucide-react";
import { API, apiFetch } from "@/portal/api";
import { fmtCZK } from "@/portal/components/adminShared";
import { toast } from "@/components/ui/sonner";
import { confirmDialog } from "@/portal/confirm";
import CarSelect from "@/portal/components/CarSelect";
import ContractRow from "@/portal/components/ContractRow";
import InvoiceSection from "@/portal/components/InvoiceSection";
import CommunicationLog from "@/portal/components/CommunicationLog";
import DateInput from "@/portal/components/DateInput";
import { useAuth } from "@/portal/AuthContext";

// ARES typeahead: type a company name, pick a match -> fills name/ICO/DIC/address.
const AresCompanySearch = ({ value, onChange, onPick, token }) => {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const skipNext = useRef(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (skipNext.current) { skipNext.current = false; return; }
    const q = (value || "").trim();
    if (q.length < 3) { setResults([]); setOpen(false); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const d = await apiFetch(`/admin/ares/search?q=${encodeURIComponent(q)}`, { token });
        setResults(d.results || []);
        setOpen((d.results || []).length > 0);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer.current);
  }, [value, token]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (r) => {
    skipNext.current = true;
    setOpen(false);
    setResults([]);
    onPick(r);
  };

  return (
    <div className="relative" ref={boxRef}>
      <input
        className="input-dark w-full pr-9"
        placeholder="Název firmy (našeptávač ARES)"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => results.length > 0 && setOpen(true)}
        required
        autoComplete="off"
        data-testid="client-company-name"
      />
      {loading && <Loader2 className="w-4 h-4 animate-spin text-zinc-500 absolute right-3 top-1/2 -translate-y-1/2" />}
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 rounded-xl border border-white/10 bg-[#141414] shadow-2xl shadow-black/60 overflow-hidden" data-testid="ares-search-dropdown">
          {results.map((r) => (
            <button
              key={r.ico}
              type="button"
              onClick={() => pick(r)}
              className="w-full text-left px-3.5 py-2.5 hover:bg-indigo-500/10 transition-colors border-b border-white/5 last:border-0"
              data-testid={`ares-search-option-${r.ico}`}
            >
              <p className="text-sm text-zinc-100 flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5 text-indigo-400 shrink-0" /> {r.name}
              </p>
              <p className="text-[11px] text-zinc-500 mt-0.5 pl-5">IČO: {r.ico}{r.address ? ` · ${r.address}` : ""}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const emptyClient = {
  client_type: "individual",
  first_name: "", last_name: "", birth_date: "", address: "", email: "", phone: "",
  id_card_number: "", license_number: "",
  company_name: "", ico: "", dic: "", contact_name: "", driver_name: "",
  car_id: "", license_plate: "", vin: "", amount: "", deposit: "", date_from: "", date_to: "",
  invoice_months: "", invoice_price: "", invoice_due: "", advance_payment: false,
  duration_months: "", duration_unit: "mesic", duration_count: "", notice_period: "1", payment_day: "", km_limit: "", km_over_price: "", sign_place: "Praha",
};

// Warning shown when the selected car is in service or already has an active rental.
const CarOccupancyNote = ({ cars, carId }) => {
  const car = cars.find((c) => c.id === carId);
  if (!car) return null;
  if (car.status === "service") {
    return (
      <p className="text-[11px] text-red-300 mt-1.5 flex items-center gap-1.5" data-testid="car-occupancy-warning">
        ⚠ Tento vůz je označen jako <strong>v servisu</strong>. Opravdu ho chcete přiřadit?
      </p>
    );
  }
  if (car.active_rentals > 0) {
    return (
      <p className="text-[11px] text-amber-300 mt-1.5 flex items-center gap-1.5" data-testid="car-occupancy-warning">
        ⚠ Tento vůz má již <strong>{car.active_rentals}× aktivní pronájem</strong>. Zkontrolujte dostupnost.
      </p>
    );
  }
  return null;
};

const ClientsTab = ({ clients, cars, token, onChange }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState(emptyClient);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [aresLoading, setAresLoading] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const ocrFileRef = useRef(null);

  const runOcr = async (fileList) => {
    const files = Array.from(fileList || []).slice(0, 3);
    if (!files.length) return;
    setOcrLoading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const res = await fetch(`${API}/admin/clients/ocr-id`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error((data && data.detail) || "Doklad se nepodařilo přečíst.");
      setForm((f) => {
        const isCompany = f.client_type === "company";
        const fullName = [data.first_name, data.last_name].filter(Boolean).join(" ").trim();
        return {
          ...f,
          first_name: data.first_name || f.first_name,
          last_name: data.last_name || f.last_name,
          birth_date: data.birth_date || f.birth_date,
          address: data.address || f.address,
          id_card_number: data.id_card_number || f.id_card_number,
          license_number: data.license_number || f.license_number,
          driver_name: isCompany ? (fullName || f.driver_name) : f.driver_name,
        };
      });
      toast.success("Údaje z dokladů doplněny. Zkontrolujte je před uložením.");
    } catch (e) { toast.error(e.message); }
    finally { setOcrLoading(false); if (ocrFileRef.current) ocrFileRef.current.value = ""; }
  };

  const lookupAres = async () => {
    const ico = (form.ico || "").replace(/\D/g, "");
    if (ico.length !== 8) { toast.error("Zadejte platné IČO (8 číslic)."); return; }
    setAresLoading(true);
    try {
      const d = await apiFetch(`/admin/ares/${ico}`, { token });
      setForm((f) => ({
        ...f,
        company_name: d.name || f.company_name,
        dic: d.dic || f.dic,
        address: d.address || f.address,
      }));
      toast.success("Údaje z ARES doplněny.");
    } catch (e) { toast.error(e.message || "ARES lookup selhal."); }
    finally { setAresLoading(false); }
  };
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [subTab, setSubTab] = useState("rentals");

  const today = new Date().toISOString().slice(0, 10);
  const unpaidInfo = (cl) => {
    const open = (cl.invoices || []).filter((i) => i.status !== "paid");
    const overdue = open.filter((i) => i.due_on && i.due_on < today);
    return { open: open.length, overdue: overdue.length };
  };

  const q = search.trim().toLowerCase();
  const filteredClients = clients.filter((cl) => {
    if (filter === "company" && cl.client_type !== "company") return false;
    if (filter === "individual" && cl.client_type === "company") return false;
    if (filter === "active" && !(cl.rentals || []).length) return false;
    if (filter === "debt" && unpaidInfo(cl).overdue === 0) return false;
    if (!q) return true;
    const hay = [
      cl.name, cl.email, cl.phone, cl.company_name, cl.ico,
      ...(cl.rentals || []).map((r) => r.license_plate),
      ...(cl.rentals || []).map((r) => r.car && `${r.car.brand} ${r.car.model}`),
    ].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  });

  const selected = selectedId ? clients.find((c) => c.id === selectedId) : null;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    if (!form.car_id) { setError("Vyberte vozidlo z číselníku."); return; }
    if (form.advance_payment) {
      if (!form.invoice_months || Number(form.invoice_months) <= 0) { setError("U platby dopředu vyplňte počet měsíců."); return; }
      if (!form.invoice_due) { setError("U platby dopředu vyplňte splatnost faktury."); return; }
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: form.amount ? Number(form.amount) : null,
        deposit: form.deposit ? Number(form.deposit) : null,
        advance_payment: !!form.advance_payment,
        invoice_months: form.advance_payment && form.invoice_months ? Number(form.invoice_months) : null,
        invoice_unit_price: form.advance_payment && form.invoice_price ? Number(form.invoice_price) : null,
        invoice_due_on: form.advance_payment ? (form.invoice_due || null) : null,
        duration_unit: form.duration_unit || "mesic",
        duration_count: form.duration_count ? Number(form.duration_count) : null,
        duration_months: (form.duration_unit || "mesic") === "mesic" && form.duration_count ? Number(form.duration_count) : (form.duration_months ? Number(form.duration_months) : null),
        notice_period: form.notice_period ? Number(form.notice_period) : 1,
        payment_day: form.payment_day ? Number(form.payment_day) : null,
        km_limit: form.km_limit ? Number(form.km_limit) : null,
        km_over_price: form.km_over_price ? Number(form.km_over_price) : null,
      };
      const res = await apiFetch("/admin/clients", { method: "POST", token, body: payload });
      setResult(res);
      setForm(emptyClient);
      onChange();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const resend = async (id) => {
    try {
      const r = await apiFetch(`/admin/clients/${id}/resend-invite`, { method: "POST", token });
      if (r.invite_sent) toast.success("Pozvánka odeslána.");
      else toast.error("Pozvánku se nepodařilo odeslat (zkontrolujte e-mailovou doménu v Resend).");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const removeClient = async (cl) => {
    const ok = await confirmDialog({
      title: `Smazat klienta ${cl.name}?`,
      description: "Smažou se i jeho smlouvy, faktury, protokoly a fotky. Akce je nevratná.",
      confirmText: "Smazat klienta", danger: true,
    });
    if (!ok) return;
    try {
      await apiFetch(`/admin/clients/${cl.id}`, { method: "DELETE", token });
      toast.success("Klient smazán.");
      onChange();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      {/* Form */}
      <div className="lg:col-span-2">
        <div className="glass rounded-2xl p-6">
          <h3 className="font-['Cabinet_Grotesk'] text-lg font-bold mb-4">Nový klient & pronájem</h3>

          {cars.length === 0 && (
            <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs flex items-center gap-2">
              <Plus className="w-4 h-4 flex-shrink-0" /> Nejdřív přidejte vozidlo do číselníku (záložka Vozový park).
            </div>
          )}

          {result && (
            <div className="mb-4 p-4 rounded-xl bg-green-500/10 border border-green-500/20" data-testid="client-create-result">
              <p className="text-green-400 font-medium text-sm flex items-center gap-2 mb-1">
                <CheckCircle2 className="w-4 h-4" /> Klient a pronájem uloženy
              </p>
              <p className="text-zinc-400 text-xs flex items-center gap-1.5">
                {result.invite_sent
                  ? <><MailCheck className="w-3.5 h-3.5 text-green-400" /> Pozvánka k nastavení hesla odeslána na {result.client.email}.</>
                  : <><MailWarning className="w-3.5 h-3.5 text-amber-400" /> Účet vytvořen, ale e-mail se nepodařilo odeslat (ověřte doménu v Resend).</>}
              </p>
              {result.invoice && (
                <p className="text-zinc-400 text-xs mt-1" data-testid="create-invoice-result">
                  Faktura {result.invoice.number} na {Number(result.invoice.amount).toLocaleString("cs-CZ")} Kč vystavena.
                </p>
              )}
              {result.invoice_error && (
                <p className="text-amber-400 text-xs mt-1" data-testid="create-invoice-error">Fakturu se nepodařilo vystavit: {result.invoice_error}. Vystavte ji v detailu klienta.</p>
              )}
            </div>
          )}

          <form onSubmit={submit} className="space-y-3" data-testid="client-form">
            {/* Typ klienta */}
            <div className="flex gap-2 p-1 bg-white/5 rounded-full" data-testid="client-type-toggle">
              <button
                type="button"
                onClick={() => setForm({ ...form, client_type: "individual" })}
                className={`flex-1 text-xs font-medium py-1.5 rounded-full transition-colors ${form.client_type !== "company" ? "bg-indigo-500 text-white" : "text-zinc-400 hover:text-white"}`}
                data-testid="client-type-individual"
              >
                Fyzická osoba
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, client_type: "company" })}
                className={`flex-1 text-xs font-medium py-1.5 rounded-full transition-colors ${form.client_type === "company" ? "bg-indigo-500 text-white" : "text-zinc-400 hover:text-white"}`}
                data-testid="client-type-company"
              >
                Firma
              </button>
            </div>

            {/* OCR doklady */}
            <div className="rounded-xl border border-orange-500/20 bg-orange-500/[0.04] p-3" data-testid="ocr-id-panel">
              <p className="text-[11px] uppercase tracking-wider text-orange-300 font-semibold flex items-center gap-1.5 mb-1">
                <ScanLine className="w-3.5 h-3.5" /> Načíst z dokladů (foto)
              </p>
              <p className="text-[11px] text-zinc-500 mb-2">Nahraj fotku/sken občanky a řidičáku – AI vyplní jméno, datum narození, adresu a čísla dokladů. Vše lze před uložením upravit.</p>
              <input ref={ocrFileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => runOcr(e.target.files)} data-testid="ocr-id-input" />
              <button type="button" onClick={() => ocrFileRef.current?.click()} disabled={ocrLoading} className="btn-accent inline-flex items-center gap-2 disabled:opacity-60 text-xs" data-testid="ocr-id-btn">
                {ocrLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ScanLine className="w-3.5 h-3.5" />} {ocrLoading ? "AI čte doklady…" : "Nahrát doklady a vyplnit"}
              </button>
            </div>

            {form.client_type === "company" ? (
              <>
                <AresCompanySearch
                  value={form.company_name}
                  onChange={(v) => setForm((f) => ({ ...f, company_name: v }))}
                  onPick={(d) => {
                    setForm((f) => ({
                      ...f,
                      company_name: d.name || f.company_name,
                      ico: d.ico || f.ico,
                      dic: d.dic || f.dic,
                      address: d.address || f.address,
                    }));
                    toast.success("Údaje firmy doplněny z ARES.");
                  }}
                  token={token}
                />
                <div className="grid grid-cols-[1fr_auto] gap-2 items-start">
                  <input className="input-dark" placeholder="IČO (8 číslic)" value={form.ico} onChange={(e) => setForm({ ...form, ico: e.target.value })} required data-testid="client-ico" />
                  <button type="button" onClick={lookupAres} disabled={aresLoading} className="btn-accent inline-flex items-center gap-2 whitespace-nowrap disabled:opacity-60 h-[42px]" data-testid="ares-lookup-btn">
                    {aresLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Načíst z ARES
                  </button>
                </div>
                <input className="input-dark" placeholder="DIČ (doplní ARES)" value={form.dic} onChange={(e) => setForm({ ...form, dic: e.target.value })} data-testid="client-dic" />
                <input className="input-dark" placeholder="Sídlo firmy (adresa)" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="client-company-address" />
                <input className="input-dark" placeholder="Kontaktní osoba (jméno)" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="client-contact-name" />
                <input type="email" className="input-dark" placeholder="E-mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="client-email" />
                <input className="input-dark" placeholder="Telefon" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="client-phone" />
                <div className="border-t border-white/10 pt-3">
                  <p className="text-[11px] text-zinc-500 mb-2">Řidič vozidla (fyzická osoba)</p>
                  <input className="input-dark mb-3" placeholder="Jméno řidiče" value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })} data-testid="client-driver-name" />
                  <div className="grid grid-cols-2 gap-3">
                    <input className="input-dark" placeholder="Číslo OP / pasu řidiče" value={form.id_card_number} onChange={(e) => setForm({ ...form, id_card_number: e.target.value })} data-testid="client-id-card" />
                    <input className="input-dark" placeholder="Číslo ŘP řidiče" value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} data-testid="client-license" />
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <input className="input-dark" placeholder="Jméno" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required data-testid="client-first-name" />
                  <input className="input-dark" placeholder="Příjmení" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required data-testid="client-last-name" />
                </div>
                <label className="block text-xs text-zinc-500">Datum narození</label>
                <DateInput value={form.birth_date} onChange={(v) => setForm({ ...form, birth_date: v })} testid="client-birth" />
                <input className="input-dark" placeholder="Adresa trvalého bydliště" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="client-address" />
                <input type="email" className="input-dark" placeholder="E-mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="client-email" />
                <input className="input-dark" placeholder="Telefon" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="client-phone" />
                <div className="grid grid-cols-2 gap-3">
                  <input className="input-dark" placeholder="Číslo OP / pasu" value={form.id_card_number} onChange={(e) => setForm({ ...form, id_card_number: e.target.value })} data-testid="client-id-card" />
                  <input className="input-dark" placeholder="Číslo ŘP" value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} data-testid="client-license" />
                </div>
              </>
            )}

            <div className="border-t border-white/10 pt-3 mt-3">
              <label className="block text-xs text-zinc-500 mb-1.5">Vozidlo (z číselníku)</label>
              <CarSelect
                cars={cars}
                value={form.car_id}
                onSelect={(v, car) => setForm({
                  ...form,
                  car_id: v,
                  license_plate: form.license_plate || (car && car.license_plate) || "",
                  vin: form.vin || (car && car.vin) || "",
                })}
                testid="client-car"
              />
              <CarOccupancyNote cars={cars} carId={form.car_id} />
              <div className="grid grid-cols-2 gap-3 mt-3">
                <input className="input-dark" placeholder="SPZ vozu" value={form.license_plate} onChange={(e) => setForm({ ...form, license_plate: e.target.value })} data-testid="client-plate" />
                <input className="input-dark" placeholder="VIN vozu" value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value })} data-testid="client-vin" />
              </div>
              <p className="text-[11px] text-zinc-600 mt-1">SPZ a VIN konkrétního vozu pro tuto smlouvu (předvyplní se z číselníku, lze přepsat).</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input type="number" className="input-dark" placeholder={`${(form.duration_unit || "mesic") === "den" ? "Denní" : (form.duration_unit === "tyden" ? "Týdenní" : "Měsíční")} nájemné (Kč)`} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="client-amount" />
              <input type="number" className="input-dark" placeholder="Kauce (Kč)" value={form.deposit} onChange={(e) => setForm({ ...form, deposit: e.target.value })} data-testid="client-deposit" />
            </div>

            {/* Fakturace hned při založení – přesunuto níže za parametry smlouvy */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Od</label>
                <DateInput value={form.date_from} onChange={(v) => setForm({ ...form, date_from: v })} testid="client-from" />
              </div>
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Do</label>
                <DateInput value={form.date_to} onChange={(v) => setForm({ ...form, date_to: v })} testid="client-to" />
              </div>
            </div>

            <div className="border-t border-white/10 pt-3 mt-3">
              <p className="text-xs uppercase tracking-wider text-indigo-400 font-semibold mb-3 flex items-center gap-1.5">
                <FileSignature className="w-3.5 h-3.5" /> Parametry smlouvy
              </p>
              <div className="mb-3">
                <label className="block text-xs text-zinc-500 mb-1">Režim pronájmu</label>
                <div className="inline-flex rounded-lg border border-white/10 bg-white/5 p-0.5" data-testid="client-duration-unit">
                  {[["den", "Denní"], ["tyden", "Týdenní"], ["mesic", "Měsíční"]].map(([val, lbl]) => (
                    <button key={val} type="button" onClick={() => setForm({ ...form, duration_unit: val })}
                      className={`px-3 py-1.5 text-xs rounded-md transition-colors ${(form.duration_unit || "mesic") === val ? "bg-orange-500 text-white" : "text-zinc-400 hover:text-white"}`}
                      data-testid={`client-unit-${val}`}>{lbl}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Doba trvání ({(form.duration_unit || "mesic") === "den" ? "dní" : (form.duration_unit === "tyden" ? "týdnů" : "měsíců")})</label>
                  <input type="number" className="input-dark" placeholder={(form.duration_unit || "mesic") === "den" ? "14" : (form.duration_unit === "tyden" ? "4" : "12")} value={form.duration_count} onChange={(e) => setForm({ ...form, duration_count: e.target.value })} data-testid="client-duration" />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Výpovědní doba (měs.)</label>
                  <input type="number" className="input-dark" placeholder="1" value={form.notice_period} onChange={(e) => setForm({ ...form, notice_period: e.target.value })} data-testid="client-notice" />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Den splatnosti</label>
                  <input type="number" className="input-dark" placeholder="15" value={form.payment_day} onChange={(e) => setForm({ ...form, payment_day: e.target.value })} data-testid="client-payment-day" />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Limit km / {(form.duration_unit || "mesic") === "den" ? "den" : (form.duration_unit === "tyden" ? "týden" : "měsíc")}</label>
                  <input type="number" className="input-dark" placeholder="3500" value={form.km_limit} onChange={(e) => setForm({ ...form, km_limit: e.target.value })} data-testid="client-km-limit" />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Cena za km navíc (Kč)</label>
                  <input type="number" step="0.1" className="input-dark" placeholder="3" value={form.km_over_price} onChange={(e) => setForm({ ...form, km_over_price: e.target.value })} data-testid="client-km-over" />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Místo podpisu</label>
                  <input className="input-dark" placeholder="Praha" value={form.sign_place} onChange={(e) => setForm({ ...form, sign_place: e.target.value })} data-testid="client-sign-place" />
                </div>
              </div>
            </div>

            {/* Platba dopředu – přizpůsobí smlouvu i vystaví fakturu */}
            <div className={`rounded-xl border p-3 transition-colors ${form.advance_payment ? "border-emerald-500/30 bg-emerald-500/5" : "border-white/10 bg-white/[0.02]"}`} data-testid="create-invoice-block">
              <label className="flex items-start gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!form.advance_payment}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setForm((f) => ({
                      ...f,
                      advance_payment: on,
                      invoice_months: on ? (f.invoice_months || ((f.duration_unit || "mesic") === "mesic" && f.duration_count ? String(f.duration_count) : "12")) : "",
                      invoice_due: on ? (f.invoice_due || f.date_from || "") : "",
                      invoice_price: on ? f.invoice_price : "",
                    }));
                  }}
                  className="mt-0.5 w-4 h-4 accent-emerald-500"
                  data-testid="advance-payment-checkbox"
                />
                <span>
                  <span className={`text-xs font-semibold ${form.advance_payment ? "text-emerald-300" : "text-zinc-300"}`}>Klient platí dopředu</span>
                  <span className="block text-[11px] text-zinc-500 mt-0.5">Smlouva bude obsahovat ujednání o jednorázové platbě předem a rovnou se vystaví faktura.</span>
                </span>
              </label>

              {form.advance_payment && (
                <div className="mt-3 space-y-2" data-testid="advance-payment-fields">
                  <div className="flex flex-wrap gap-1.5">
                    {[[1, "1 měs."], [3, "3 měs."], [6, "6 měs."], [12, "Rok dopředu"]].map(([val, lbl]) => (
                      <button key={val} type="button" onClick={() => setForm({ ...form, invoice_months: String(val) })}
                        className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${Number(form.invoice_months) === val ? "border-emerald-500 bg-emerald-500/15 text-emerald-300" : "border-white/10 text-zinc-400 hover:text-white"}`}
                        data-testid={`create-invoice-preset-${val}`}>{lbl}</button>
                    ))}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-[10px] text-zinc-500 mb-0.5">Měsíců dopředu</label>
                      <input type="number" min="1" max="24" className="input-dark text-xs w-full" value={form.invoice_months} onChange={(e) => setForm({ ...form, invoice_months: e.target.value })} data-testid="create-invoice-months" />
                    </div>
                    <div>
                      <label className="block text-[10px] text-zinc-500 mb-0.5">Cena/měs (Kč)</label>
                      <input type="number" min="1" className="input-dark text-xs w-full" placeholder={form.amount ? `${form.amount} (dle smlouvy)` : "dle smlouvy"} value={form.invoice_price} onChange={(e) => setForm({ ...form, invoice_price: e.target.value })} title="Vlastní zvýhodněná cena za měsíc – prázdné = nájemné výše" data-testid="create-invoice-price" />
                    </div>
                    <div>
                      <label className="block text-[10px] text-zinc-500 mb-0.5">Splatnost faktury</label>
                      <DateInput value={form.invoice_due} onChange={(v) => setForm({ ...form, invoice_due: v })} className="text-xs w-full" testid="create-invoice-due" />
                    </div>
                  </div>
                  {Number(form.invoice_months) > 0 && Number(form.invoice_price || form.amount) > 0 && (
                    <p className="text-[11px] text-emerald-400/90" data-testid="create-invoice-preview">
                      Faktura bude na {(Number(form.invoice_months) * Number(form.invoice_price || form.amount)).toLocaleString("cs-CZ")} Kč
                      {" "}({form.invoice_months}× {Number(form.invoice_price || form.amount).toLocaleString("cs-CZ")} Kč{Number(form.invoice_months) === 12 ? " – platba na rok dopředu" : ""})
                      {" "}· smlouva uvede jednorázovou úhradu předem
                    </p>
                  )}
                </div>
              )}
            </div>

            {error && <p className="text-red-400 text-sm">{error}</p>}

            <button type="submit" disabled={saving || cars.length === 0} className="btn-accent w-full inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="client-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Uložit, vytvořit smlouvu a pozvat klienta
            </button>
          </form>
        </div>
      </div>

      {/* List / Detail */}
      <div className="lg:col-span-3">
        {selected ? (
          /* ---------- DETAIL KLIENTA ---------- */
          <div data-testid={`client-detail-${selected.id}`}>
            <button onClick={() => { setSelectedId(null); setEditId(null); }} className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white mb-3" data-testid="client-detail-back">
              ← Zpět na seznam
            </button>
            <div className="glass rounded-2xl p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-['Cabinet_Grotesk'] text-xl font-bold flex items-center gap-2">
                    {selected.name}
                    {selected.client_type === "company" && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-400 border border-sky-500/20" data-testid={`client-company-badge-${selected.id}`}>
                        <Building2 className="w-2.5 h-2.5" /> Firma
                      </span>
                    )}
                  </p>
                  {selected.client_type === "company" && (selected.ico || selected.dic) && (
                    <p className="text-xs text-zinc-500 mt-0.5">IČO: {selected.ico || "—"}{selected.dic ? ` · DIČ: ${selected.dic}` : ""}{selected.contact_name ? ` · ${selected.contact_name}` : ""}</p>
                  )}
                  <p className="text-xs text-zinc-500 flex items-center gap-1.5 mt-0.5"><Mail className="w-3 h-3" /> {selected.email}{selected.phone ? ` · ${selected.phone}` : ""}</p>
                </div>
                <div className="flex items-center gap-2">
                  {selected.has_password ? (
                    <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-green-500/15 text-green-400 border border-green-500/20">
                      <KeyRound className="w-3 h-3" /> Heslo nastaveno
                    </span>
                  ) : (
                    <>
                      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/20">Čeká na nastavení hesla</span>
                      <button onClick={() => resend(selected.id)} className="text-zinc-400 hover:text-white p-1.5" title="Znovu odeslat pozvánku" data-testid={`resend-${selected.id}`}>
                        <RefreshCw className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                  <button onClick={() => removeClient(selected)} className="text-zinc-500 hover:text-red-400 p-1.5" title="Smazat klienta" data-testid={`client-delete-${selected.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Sub-tabs */}
              <div className="flex flex-wrap gap-1.5 mt-4 border-b border-white/10 pb-3" data-testid="client-subtabs">
                {[["rentals", "Pronájmy & smlouvy"], ["invoices", "Fakturace"], ["comm", "Komunikace"], ["profile", "Údaje klienta"]].map(([val, lbl]) => (
                  <button key={val} onClick={() => setSubTab(val)}
                    className={`text-xs px-3.5 py-1.5 rounded-full transition-colors ${subTab === val ? "bg-indigo-500 text-white" : "text-zinc-400 hover:text-white border border-white/10"}`}
                    data-testid={`client-subtab-${val}`}>{lbl}</button>
                ))}
              </div>

              {subTab === "rentals" && (
                <div data-testid="client-section-rentals">
                  {selected.rentals && selected.rentals.length > 0 ? (
                    <div className="mt-3 space-y-2">
                      {selected.rentals.map((r) => (
                        <RentalRow key={r.id} rental={r} cars={cars} token={token} onChange={onChange} />
                      ))}
                    </div>
                  ) : (
                    <p className="text-zinc-500 text-sm mt-3">Žádné pronájmy.</p>
                  )}
                  <AddCarForm clientId={selected.id} cars={cars} token={token} onChange={onChange} />
                  {selected.contracts && selected.contracts.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-white/5 space-y-3">
                      {selected.contracts.map((ct) => (
                        <ContractRow key={ct.id} contract={ct} token={token} onChange={onChange} />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {subTab === "invoices" && (
                <div data-testid="client-section-invoices">
                  {isAdmin && selected.rentals && selected.rentals.length > 0 ? (
                    <InvoiceSection client={selected} token={token} onChange={onChange} />
                  ) : (
                    <p className="text-zinc-500 text-sm mt-3">{isAdmin ? "Klient nemá žádný pronájem – fakturace se odemkne po přidání vozu." : "Fakturaci spravuje administrátor."}</p>
                  )}
                </div>
              )}

              {subTab === "comm" && (
                <div data-testid="client-section-comm">
                  <CommunicationLog clientId={selected.id} token={token} />
                </div>
              )}

              {subTab === "profile" && (
                <div data-testid="client-section-profile">
                  <EditClientForm client={selected} token={token} onChange={onChange} onClose={() => setSubTab("rentals")} />
                </div>
              )}
            </div>
          </div>
        ) : (
          /* ---------- SEZNAM KLIENTŮ ---------- */
          <>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <h3 className="font-['Cabinet_Grotesk'] text-lg font-bold">Klienti ({filteredClients.length}{filteredClients.length !== clients.length ? `/${clients.length}` : ""})</h3>
              <div className="relative w-full sm:w-72">
                <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Hledat (jméno, e-mail, IČO, SPZ…)"
                  className="input-dark pl-9 pr-9 w-full"
                  data-testid="clients-search"
                />
                {search && (
                  <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white" title="Vymazat hledání" data-testid="clients-search-clear">
                    <XIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-4" data-testid="clients-filters">
              {[["all", "Vše"], ["company", "Firmy"], ["individual", "Osoby"], ["active", "S pronájmem"], ["debt", "Po splatnosti"]].map(([val, lbl]) => (
                <button key={val} onClick={() => setFilter(val)}
                  className={`text-xs px-3 py-1.5 rounded-full transition-colors ${filter === val ? "bg-indigo-500 text-white" : "text-zinc-400 hover:text-white border border-white/10"}`}
                  data-testid={`clients-filter-${val}`}>{lbl}</button>
              ))}
            </div>
            {clients.length === 0 ? (
              <div className="glass rounded-2xl p-8 text-center text-zinc-500">Zatím žádní klienti.</div>
            ) : filteredClients.length === 0 ? (
              <div className="glass rounded-2xl p-8 text-center text-zinc-500" data-testid="clients-no-results">Žádný klient neodpovídá {q ? `hledání „${search}"` : "zvolenému filtru"}.</div>
            ) : (
              <div className="glass rounded-2xl divide-y divide-white/5 overflow-hidden" data-testid="clients-list">
                {filteredClients.map((cl) => {
                  const u = unpaidInfo(cl);
                  return (
                    <button
                      key={cl.id}
                      onClick={() => { setSelectedId(cl.id); setSubTab("rentals"); }}
                      className="w-full text-left px-4 py-3 hover:bg-white/[0.04] transition-colors flex flex-wrap items-center gap-x-4 gap-y-1"
                      data-testid={`client-row-${cl.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm flex items-center gap-2 truncate">
                          {cl.name}
                          {cl.client_type === "company" && (
                            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-400 border border-sky-500/20 shrink-0">
                              <Building2 className="w-2.5 h-2.5" /> Firma
                            </span>
                          )}
                        </p>
                        <p className="text-[11px] text-zinc-500 truncate">{cl.email}{cl.phone ? ` · ${cl.phone}` : ""}{cl.ico ? ` · IČO ${cl.ico}` : ""}</p>
                      </div>
                      <span className="text-[11px] text-zinc-400 inline-flex items-center gap-1 shrink-0">
                        <Car className="w-3 h-3 text-indigo-400" /> {(cl.rentals || []).length}× pronájem
                      </span>
                      {u.overdue > 0 ? (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/20 shrink-0" data-testid={`client-overdue-${cl.id}`}>{u.overdue} po splatnosti</span>
                      ) : u.open > 0 ? (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/20 shrink-0">{u.open} nezaplaceno</span>
                      ) : (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 border border-green-500/20 shrink-0">OK</span>
                      )}
                      {!cl.has_password && <span className="text-[11px] text-amber-400/80 shrink-0" title="Čeká na nastavení hesla">⏳ heslo</span>}
                      <span className="text-zinc-600 shrink-0">›</span>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ---------------- Edit existing client's profile ----------------
const EditClientForm = ({ client, token, onChange, onClose }) => {
  const [f, setF] = useState({
    client_type: client.client_type || "individual",
    first_name: client.first_name || "",
    last_name: client.last_name || "",
    birth_date: client.birth_date || "",
    address: client.address || "",
    email: client.email || "",
    phone: client.phone || "",
    id_card_number: client.id_card_number || "",
    license_number: client.license_number || "",
    company_name: client.company_name || "",
    ico: client.ico || "",
    dic: client.dic || "",
    contact_name: client.contact_name || "",
    driver_name: client.driver_name || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      const body = { ...f };
      if (!body.email || !body.email.trim()) delete body.email;
      await apiFetch(`/admin/clients/${client.id}`, { method: "PUT", token, body });
      onChange();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const isCompany = f.client_type === "company";

  return (
    <div className="mt-3 rounded-xl border border-indigo-500/20 bg-indigo-500/5 p-4 space-y-3" data-testid={`edit-client-form-${client.id}`}>
      <p className="text-xs uppercase tracking-wider text-indigo-300 font-semibold flex items-center gap-1.5">
        <Pencil className="w-3.5 h-3.5" /> Úprava údajů klienta
      </p>

      <div className="flex gap-2 p-1 bg-white/5 rounded-full">
        <button type="button" onClick={() => setF({ ...f, client_type: "individual" })}
          className={`flex-1 text-xs font-medium py-1.5 rounded-full transition-colors ${!isCompany ? "bg-indigo-500 text-white" : "text-zinc-400 hover:text-white"}`}
          data-testid={`edit-client-type-individual-${client.id}`}>Fyzická osoba</button>
        <button type="button" onClick={() => setF({ ...f, client_type: "company" })}
          className={`flex-1 text-xs font-medium py-1.5 rounded-full transition-colors ${isCompany ? "bg-indigo-500 text-white" : "text-zinc-400 hover:text-white"}`}
          data-testid={`edit-client-type-company-${client.id}`}>Firma</button>
      </div>

      {isCompany ? (
        <>
          <input className="input-dark" placeholder="Název firmy" value={f.company_name} onChange={(e) => setF({ ...f, company_name: e.target.value })} data-testid={`edit-company-name-${client.id}`} />
          <div className="grid grid-cols-2 gap-2">
            <input className="input-dark" placeholder="IČO" value={f.ico} onChange={(e) => setF({ ...f, ico: e.target.value })} data-testid={`edit-ico-${client.id}`} />
            <input className="input-dark" placeholder="DIČ (nepovinné)" value={f.dic} onChange={(e) => setF({ ...f, dic: e.target.value })} data-testid={`edit-dic-${client.id}`} />
          </div>
          <input className="input-dark" placeholder="Sídlo firmy (adresa)" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid={`edit-company-address-${client.id}`} />
          <input className="input-dark" placeholder="Kontaktní osoba" value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} data-testid={`edit-contact-name-${client.id}`} />
          <div className="grid grid-cols-2 gap-2">
            <input type="email" className="input-dark" placeholder="E-mail" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid={`edit-email-${client.id}`} />
            <input className="input-dark" placeholder="Telefon" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid={`edit-phone-${client.id}`} />
          </div>
          <div className="border-t border-white/10 pt-2">
            <p className="text-[11px] text-zinc-500 mb-2">Řidič vozidla</p>
            <input className="input-dark mb-2" placeholder="Jméno řidiče" value={f.driver_name} onChange={(e) => setF({ ...f, driver_name: e.target.value })} data-testid={`edit-driver-name-${client.id}`} />
            <div className="grid grid-cols-2 gap-2">
              <input className="input-dark" placeholder="Číslo OP / pasu řidiče" value={f.id_card_number} onChange={(e) => setF({ ...f, id_card_number: e.target.value })} data-testid={`edit-id-card-${client.id}`} />
              <input className="input-dark" placeholder="Číslo ŘP řidiče" value={f.license_number} onChange={(e) => setF({ ...f, license_number: e.target.value })} data-testid={`edit-license-${client.id}`} />
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2">
            <input className="input-dark" placeholder="Jméno" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} data-testid={`edit-first-name-${client.id}`} />
            <input className="input-dark" placeholder="Příjmení" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} data-testid={`edit-last-name-${client.id}`} />
          </div>
          <div>
            <label className="block text-[11px] text-zinc-500 mb-1">Datum narození</label>
            <DateInput value={f.birth_date} onChange={(v) => setF({ ...f, birth_date: v })} testid={`edit-birth-${client.id}`} />
          </div>
          <input className="input-dark" placeholder="Adresa trvalého bydliště" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid={`edit-address-${client.id}`} />
          <div className="grid grid-cols-2 gap-2">
            <input type="email" className="input-dark" placeholder="E-mail" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid={`edit-email-${client.id}`} />
            <input className="input-dark" placeholder="Telefon" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid={`edit-phone-${client.id}`} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input className="input-dark" placeholder="Číslo OP / pasu" value={f.id_card_number} onChange={(e) => setF({ ...f, id_card_number: e.target.value })} data-testid={`edit-id-card-${client.id}`} />
            <input className="input-dark" placeholder="Číslo ŘP" value={f.license_number} onChange={(e) => setF({ ...f, license_number: e.target.value })} data-testid={`edit-license-${client.id}`} />
          </div>
        </>
      )}

      <p className="text-[11px] text-zinc-600">Pozn.: úprava se projeví u nových smluv. Již vystavené (podepsané) smlouvy zůstávají beze změny.</p>
      {error && <p className="text-red-400 text-xs">{error}</p>}

      <div className="flex gap-2">
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 text-xs px-4 py-2 rounded-full bg-indigo-500 text-white hover:bg-indigo-600 disabled:opacity-60" data-testid={`edit-client-save-${client.id}`}>
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Uložit změny
        </button>
        <button onClick={onClose} className="text-xs px-4 py-2 rounded-full border border-white/10 text-zinc-400 hover:bg-white/5" data-testid={`edit-client-cancel-${client.id}`}>Zrušit</button>
      </div>
    </div>
  );
};

// ---------------- Rental row with inline edit of rental params ----------------
const RentalRow = ({ rental, cars, token, onChange }) => {
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const remove = async () => {
    const ok = await confirmDialog({
      title: "Smazat tento pronájem?",
      description: `${rental.car ? `${rental.car.brand} ${rental.car.model}` : "Pronájem"} – smaže se i navázaná smlouva, faktury a protokoly. Akce je nevratná.`,
      confirmText: "Smazat pronájem", danger: true,
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await apiFetch(`/admin/rentals/${rental.id}`, { method: "DELETE", token });
      toast.success("Pronájem smazán.");
      onChange();
    } catch (e) {
      toast.error(e.message);
      setDeleting(false);
    }
  };

  return (
    <div data-testid={`rental-row-${rental.id}`}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-zinc-400">
        <span className="inline-flex items-center gap-1.5 text-zinc-200">
          <Car className="w-3.5 h-3.5 text-indigo-400" />
          {rental.car ? `${rental.car.brand} ${rental.car.model}` : "—"}{rental.license_plate ? ` (${rental.license_plate})` : ""}
        </span>
        <span>{fmtCZK(rental.amount)}/měs.</span>
        <span className="text-zinc-500">kauce {fmtCZK(rental.deposit)}</span>
        <span className="text-zinc-500">{rental.date_from || "?"} → {rental.date_to || "?"}</span>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => setOpen(!open)} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/10 transition-colors" data-testid={`rental-edit-${rental.id}`}>
            <Pencil className="w-3 h-3" /> Upravit smlouvu
          </button>
          <button onClick={remove} disabled={deleting} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border border-red-500/30 text-red-300 hover:bg-red-500/10 transition-colors disabled:opacity-50" title="Smazat pronájem" data-testid={`rental-delete-${rental.id}`}>
            {deleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />} Smazat
          </button>
        </div>
      </div>
      {open && (
        <EditRentalForm rental={rental} cars={cars} token={token} onChange={onChange} onClose={() => setOpen(false)} />
      )}
    </div>
  );
};

const EditRentalForm = ({ rental, cars, token, onChange, onClose }) => {
  const [f, setF] = useState({
    car_id: rental.car_id || "",
    license_plate: rental.license_plate || "",
    vin: rental.vin || "",
    amount: rental.amount ?? "",
    deposit: rental.deposit ?? "",
    date_from: rental.date_from || "",
    date_to: rental.date_to || "",
    duration_months: rental.duration_months ?? "",
    notice_period: rental.notice_period ?? "1",
    payment_day: rental.payment_day ?? "",
    km_limit: rental.km_limit ?? "",
    km_over_price: rental.km_over_price ?? "",
    sign_place: rental.sign_place || "Praha",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const save = async () => {
    setError(null);
    setInfo(null);
    setSaving(true);
    try {
      const body = {
        ...f,
        amount: f.amount !== "" ? Number(f.amount) : null,
        deposit: f.deposit !== "" ? Number(f.deposit) : null,
        duration_months: f.duration_months !== "" ? Number(f.duration_months) : null,
        notice_period: f.notice_period !== "" ? Number(f.notice_period) : 1,
        payment_day: f.payment_day !== "" ? Number(f.payment_day) : null,
        km_limit: f.km_limit !== "" ? Number(f.km_limit) : null,
        km_over_price: f.km_over_price !== "" ? Number(f.km_over_price) : null,
      };
      const res = await apiFetch(`/admin/rentals/${rental.id}`, { method: "PUT", token, body });
      if (res.contract_updated) {
        setInfo("Uloženo. Smlouva (PDF) byla přegenerována s novými údaji. U podepsané smlouvy zůstal zachován podpis klienta.");
        onChange();
      } else {
        onChange();
        onClose();
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 rounded-xl border border-indigo-500/20 bg-indigo-500/5 p-3 space-y-2" data-testid={`edit-rental-form-${rental.id}`}>
      <p className="text-xs uppercase tracking-wider text-indigo-300 font-semibold flex items-center gap-1.5">
        <FileSignature className="w-3.5 h-3.5" /> Úprava parametrů pronájmu / smlouvy
      </p>
      <CarSelect
        cars={cars}
        value={f.car_id}
        onSelect={(v, c) => setF({ ...f, car_id: v, license_plate: (c && c.license_plate) || f.license_plate, vin: (c && c.vin) || f.vin })}
        testid={`edit-rental-car-${rental.id}`}
      />
      <CarOccupancyNote cars={cars} carId={f.car_id} />
      <div className="grid grid-cols-2 gap-2">
        <input className="input-dark" placeholder="SPZ" value={f.license_plate} onChange={(e) => setF({ ...f, license_plate: e.target.value })} data-testid={`edit-rental-plate-${rental.id}`} />
        <input className="input-dark" placeholder="VIN" value={f.vin} onChange={(e) => setF({ ...f, vin: e.target.value })} data-testid={`edit-rental-vin-${rental.id}`} />
        <input type="number" className="input-dark" placeholder="Měsíční nájemné (Kč)" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid={`edit-rental-amount-${rental.id}`} />
        <input type="number" className="input-dark" placeholder="Kauce (Kč)" value={f.deposit} onChange={(e) => setF({ ...f, deposit: e.target.value })} data-testid={`edit-rental-deposit-${rental.id}`} />
        <input type="number" className="input-dark" placeholder="Doba (měsíců)" value={f.duration_months} onChange={(e) => setF({ ...f, duration_months: e.target.value })} data-testid={`edit-rental-duration-${rental.id}`} />
        <input type="number" className="input-dark" placeholder="Výpovědní doba (měs.)" value={f.notice_period} onChange={(e) => setF({ ...f, notice_period: e.target.value })} data-testid={`edit-rental-notice-${rental.id}`} />
        <input type="number" className="input-dark" placeholder="Den splatnosti" value={f.payment_day} onChange={(e) => setF({ ...f, payment_day: e.target.value })} data-testid={`edit-rental-payday-${rental.id}`} />
        <input type="number" className="input-dark" placeholder="Limit km / měsíc" value={f.km_limit} onChange={(e) => setF({ ...f, km_limit: e.target.value })} data-testid={`edit-rental-kmlimit-${rental.id}`} />
        <input type="number" step="0.1" className="input-dark" placeholder="Cena za km navíc" value={f.km_over_price} onChange={(e) => setF({ ...f, km_over_price: e.target.value })} data-testid={`edit-rental-kmover-${rental.id}`} />
        <input className="input-dark" placeholder="Místo podpisu" value={f.sign_place} onChange={(e) => setF({ ...f, sign_place: e.target.value })} data-testid={`edit-rental-signplace-${rental.id}`} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><label className="block text-[11px] text-zinc-500 mb-1">Od</label><DateInput value={f.date_from} onChange={(v) => setF({ ...f, date_from: v })} testid={`edit-rental-from-${rental.id}`} /></div>
        <div><label className="block text-[11px] text-zinc-500 mb-1">Do</label><DateInput value={f.date_to} onChange={(v) => setF({ ...f, date_to: v })} testid={`edit-rental-to-${rental.id}`} /></div>
      </div>
      <p className="text-[11px] text-zinc-600">Pozn.: PDF smlouvy se automaticky přegeneruje s novými údaji (např. dodatečně doplněná SPZ/VIN). U podepsané smlouvy zůstane zachován podpis klienta.</p>
      {error && <p className="text-red-400 text-xs">{error}</p>}
      {info && <p className="text-amber-300 text-xs">{info}</p>}
      <div className="flex gap-2">
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 text-xs px-4 py-2 rounded-full bg-indigo-500 text-white hover:bg-indigo-600 disabled:opacity-60" data-testid={`edit-rental-save-${rental.id}`}>
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Uložit změny
        </button>
        <button onClick={onClose} className="text-xs px-4 py-2 rounded-full border border-white/10 text-zinc-400 hover:bg-white/5" data-testid={`edit-rental-cancel-${rental.id}`}>Zrušit</button>
      </div>
    </div>
  );
};

const AddCarForm = ({ clientId, cars, token, onChange }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const empty = { car_id: "", license_plate: "", vin: "", amount: "", deposit: "", date_from: "", date_to: "", duration_months: "", notice_period: "1", payment_day: "", km_limit: "", km_over_price: "", sign_place: "Praha" };
  const [f, setF] = useState(empty);

  const save = async () => {
    if (!f.car_id) { toast.error("Vyberte vozidlo"); return; }
    setSaving(true);
    try {
      const body = {
        ...f,
        amount: f.amount ? Number(f.amount) : null,
        deposit: f.deposit ? Number(f.deposit) : null,
        duration_months: f.duration_months ? Number(f.duration_months) : null,
        notice_period: f.notice_period ? Number(f.notice_period) : 1,
        payment_day: f.payment_day ? Number(f.payment_day) : null,
        km_limit: f.km_limit ? Number(f.km_limit) : null,
        km_over_price: f.km_over_price ? Number(f.km_over_price) : null,
      };
      await apiFetch(`/admin/clients/${clientId}/rentals`, { method: "POST", token, body });
      toast.success("Vůz přidán a smlouva vytvořena.");
      setF(empty); setOpen(false); onChange();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <div className="mt-3">
      {!open ? (
        <button onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/10 transition-colors" data-testid={`add-car-toggle-${clientId}`}>
          <Plus className="w-3.5 h-3.5" /> Přidat další vůz
        </button>
      ) : (
        <div className="rounded-xl border border-indigo-500/20 bg-indigo-500/5 p-3 space-y-2" data-testid={`add-car-form-${clientId}`}>
          <p className="text-xs uppercase tracking-wider text-indigo-300 font-semibold flex items-center gap-1.5"><Car className="w-3.5 h-3.5" /> Další vůz pro tohoto klienta</p>
          <CarSelect
            cars={cars}
            value={f.car_id}
            onSelect={(v, c) => setF({ ...f, car_id: v, license_plate: (c && c.license_plate) || "", vin: (c && c.vin) || "" })}
            testid={`add-car-select-${clientId}`}
          />
          <CarOccupancyNote cars={cars} carId={f.car_id} />
          <div className="grid grid-cols-2 gap-2">
            <input className="input-dark" placeholder="SPZ" value={f.license_plate} onChange={(e) => setF({ ...f, license_plate: e.target.value })} data-testid={`add-car-plate-${clientId}`} />
            <input className="input-dark" placeholder="VIN" value={f.vin} onChange={(e) => setF({ ...f, vin: e.target.value })} data-testid={`add-car-vin-${clientId}`} />
            <input type="number" className="input-dark" placeholder="Měsíční nájemné (Kč)" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid={`add-car-amount-${clientId}`} />
            <input type="number" className="input-dark" placeholder="Kauce (Kč)" value={f.deposit} onChange={(e) => setF({ ...f, deposit: e.target.value })} data-testid={`add-car-deposit-${clientId}`} />
            <input type="number" className="input-dark" placeholder="Doba (měsíců)" value={f.duration_months} onChange={(e) => setF({ ...f, duration_months: e.target.value })} data-testid={`add-car-duration-${clientId}`} />
            <input type="number" className="input-dark" placeholder="Den splatnosti" value={f.payment_day} onChange={(e) => setF({ ...f, payment_day: e.target.value })} data-testid={`add-car-payday-${clientId}`} />
            <input type="number" className="input-dark" placeholder="Limit km / měsíc" value={f.km_limit} onChange={(e) => setF({ ...f, km_limit: e.target.value })} data-testid={`add-car-kmlimit-${clientId}`} />
            <input type="number" className="input-dark" placeholder="Cena za km navíc" value={f.km_over_price} onChange={(e) => setF({ ...f, km_over_price: e.target.value })} data-testid={`add-car-kmover-${clientId}`} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="block text-[11px] text-zinc-500 mb-1">Od</label><DateInput value={f.date_from} onChange={(v) => setF({ ...f, date_from: v })} testid={`add-car-from-${clientId}`} /></div>
            <div><label className="block text-[11px] text-zinc-500 mb-1">Do</label><DateInput value={f.date_to} onChange={(v) => setF({ ...f, date_to: v })} testid={`add-car-to-${clientId}`} /></div>
          </div>
          <div className="flex gap-2">
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 text-xs px-4 py-2 rounded-full bg-indigo-500 text-white hover:bg-indigo-600 disabled:opacity-60" data-testid={`add-car-submit-${clientId}`}>
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Přidat vůz a vytvořit smlouvu
            </button>
            <button onClick={() => { setOpen(false); setF(empty); }} className="text-xs px-4 py-2 rounded-full border border-white/10 text-zinc-400 hover:bg-white/5">Zrušit</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientsTab;
